'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import Image from 'next/image';
import {
  X, ShoppingCart, Trash2, CreditCard, Loader2, AlertCircle,
  QrCode, Building2, Wallet, Store, ChevronDown, ShoppingBag, LogIn,
  Tag, Check, AlertTriangle, Clock, ArrowLeft, RefreshCw, XCircle,
} from 'lucide-react';
import { useCart, type CartItem } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useOwnedItems } from '@/context/OwnedItemsContext';
import {
  paymentApi, PaymentApiError, isPaymentClientExpired,
  type PaymentMethodResponse, type PaymentInstructions as PaymentInstructionsType,
  type OrderPaymentMethod,
} from '@/lib/api/payments';
import { testModuleApi, courseApi, bundleApi } from '@/lib/api';
import { referralApi } from '@/lib/api/referral';
import { isKnownImageHost } from '@/lib/image-hosts';
import { CoverPlaceholder } from '@/components/ui/CoverPlaceholder';
import { useTranslations } from 'next-intl';
import { PurchaseCelebration } from '@/components/PurchaseCelebration';
import { PaymentInstructions } from '@/components/payment/PaymentInstructions';
import { useOrderStatusPoll } from '@/components/payment/useOrderStatusPoll';
import { PAYMENT_CATEGORY_ORDER, SMALL_ORDER_VA_COLLAPSE_THRESHOLD } from '@/lib/constants';

/** Maps a cart item to the shape `POST /payment/orders` and the discount-validate endpoint expect. */
function toOrderItem(item: CartItem): { testModuleId?: string; courseId?: string; bundleId?: string; eventId?: string } {
  if (item.type === 'course') return { courseId: item.moduleId };
  if (item.type === 'bundle') return { bundleId: item.moduleId };
  if (item.type === 'event') return { eventId: item.moduleId };
  return { testModuleId: item.moduleId };
}

type PurchasedType = 'module' | 'course' | 'event';

/** Bundles fold into the "module" bucket for PurchaseCelebration's CTA purposes — it
 *  only knows module/course/event. */
function purchasedTypeFromCartItem(item: CartItem): PurchasedType {
  return item.type === 'bundle' ? 'module' : (item.type ?? 'module');
}

/** OrderResponse.purchases has no 'event' productKind today (a pre-existing gap in
 *  that endpoint's shape, not introduced here — see PR description) — bundle/module/
 *  null all fold to 'module', same convention as purchasedTypeFromCartItem above. */
function purchasedTypeFromProductKind(kind: 'module' | 'course' | 'bundle' | null): PurchasedType {
  return kind === 'course' ? 'course' : 'module';
}

/**
 * Backend error strings are English and aimed at developers; a few reach the
 * buyer verbatim on the unhappy path. Map the ones we know to localized copy and
 * fall back to the generic message rather than showing raw text like
 * "Payment gateway error. Please try again." in an otherwise Indonesian UI.
 *
 * Mirrors the dashboard drawer's helper of the same name — the two checkouts
 * must not disagree about what the same failure says.
 */
function translatePaymentError(
  err: { message?: string; status?: number } | null | undefined,
  t: ReturnType<typeof useTranslations>,
): string {
  const raw = err?.message ?? '';

  // A thrown TypeError from fetch() means the request never reached the server.
  if (!err?.status && /failed to fetch|networkerror|load failed/i.test(raw)) {
    return t('paymentErrorNetwork');
  }
  if (err?.status === 503) return t('paymentErrorUnavailable');
  if (/not found/i.test(raw)) return t('paymentErrorItemMissing');
  if (/pricing not available/i.test(raw)) return t('paymentErrorPricing');
  if (/payment method/i.test(raw)) return t('paymentErrorMethod');
  if (err?.status && err.status >= 500) return t('paymentErrorServer');

  // 4xx whose message the server already localized (discount errors, email
  // verification) — pass those through untouched.
  if (err?.status && err.status < 500 && raw) return raw;

  return t('paymentError');
}

// --- Payment method helpers ---

function formatCurrency(amount: number, gratisFallback: string): string {
  return amount === 0 ? gratisFallback : `Rp ${amount.toLocaleString('id-ID')}`;
}

function formatFee(method: PaymentMethodResponse, gratisFallback: string): string {
  const parts: string[] = [];
  if (method.feeFlat > 0) parts.push(`Rp ${method.feeFlat.toLocaleString('id-ID')}`);
  if (method.feePercent > 0) parts.push(`${method.feePercent}%`);
  return parts.length > 0 ? parts.join(' + ') : gratisFallback;
}

/** Distinguishes an actual surcharge from "Gratis" — the "+ ... admin" framing is what
 *  keeps this from reading like a second price next to the method name (see
 *  DESIGN_GUIDELINES-driven fix: a bare "Rp 4.500" here looked like the item's cost). */
function formatFeeLabel(method: PaymentMethodResponse, gratisFallback: string): string {
  const hasFee = method.feeFlat > 0 || method.feePercent > 0;
  return hasFee ? `+${formatFee(method, gratisFallback)} admin` : formatFee(method, gratisFallback);
}

// `now` is passed in (rather than read via Date.now() here) so the caller's ticking
// interval state is what drives re-renders — this function itself stays a pure
// formatter, not a hidden clock read that would freeze between renders.
function formatTimeLeft(expiresAt: string, expiredLabel: string, now: number): string {
  const diff = new Date(expiresAt).getTime() - now;
  if (diff <= 0) return expiredLabel;
  const hours = Math.floor(diff / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const secs = Math.floor((diff % 60_000) / 1000);
  if (hours > 0) return `${hours}j ${mins}m ${secs}d`;
  return `${mins}m ${secs}d`;
}

const categoryIcons: Record<string, typeof CreditCard> = {
  QRIS: QrCode,
  BANK_TRANSFER: Building2,
  E_WALLET: Wallet,
  RETAIL_OUTLET: Store,
  CREDIT_CARD: CreditCard,
};

/** Fallback wait when the API's 409 envelope omits retryAfterMs (older deploy). */
const CHECKOUT_RETRY_FALLBACK_MS = 3000;

/**
 * CHECKOUT_IN_PROGRESS means another tab is mid-checkout for these same items
 * and the server refused to start a second one — the guard against the buyer
 * receiving two payment emails with two different VA numbers for one purchase.
 *
 * It is transient by construction: the in-flight attempt is about to either
 * commit an order or fail and free its claim, so the right response is to wait
 * it out once rather than show the buyer an error they can do nothing with. The
 * retry then lands on PENDING_ORDER_EXISTS, which the catch below turns into the
 * "continue that payment" panel — the outcome the buyer actually wants.
 *
 * Retried with the SAME idempotency key: this is one logical attempt, not a new
 * one, and reusing the key means a first request that DID commit before the 409
 * was read hands back that order instead of minting a second.
 *
 * Exactly one retry. If the winner is still in flight after the wait, something
 * slower than a normal gateway call is happening and the honest answer is the
 * error message, not an indefinite spinner.
 */
async function createOrderRetryingInProgress(
  payload: Parameters<typeof paymentApi.createOrder>[0],
): ReturnType<typeof paymentApi.createOrder> {
  try {
    return await paymentApi.createOrder(payload);
  } catch (err) {
    if (!(err instanceof PaymentApiError) || err.code !== 'CHECKOUT_IN_PROGRESS') throw err;
    await new Promise((resolve) => setTimeout(resolve, err.retryAfterMs ?? CHECKOUT_RETRY_FALLBACK_MS));
    return paymentApi.createOrder(payload);
  }
}

const DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL || 'http://localhost:3502';

// --- In-place order tracking (Task: show VA/QRIS instructions without leaving the page) ---

type OrderStatus = 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED' | 'REFUNDED';

interface ActiveOrder {
  id: string;
  status: OrderStatus;
  totalAmount: number;
  expiresAt: string | null;
  instructions: PaymentInstructionsType | null;
  // Bank logo/display name for the VA destination block (PaymentInstructions) — null
  // for a free order or a method with no logo on file.
  paymentMethod: OrderPaymentMethod | null;
  purchasedTitles: string[];
  purchasedTypes: PurchasedType[];
  // Which gateway actually issued this order — drives BOTH the "Batalkan Pesanan"
  // button (only MIDTRANS implements a safe cancelInvoice; see
  // domain/payment-gateway.interface.ts) and PaymentInstructions' processor label.
  // Nullable defensively (an older cached response, or a future field removal)
  // rather than because the field is normally absent — both button and label
  // render nothing rather than guess a processor.
  gateway: string | null;
  // True when a FAILED order was the buyer's own deliberate cancel (see
  // OrderResponse.cancelledByUser) rather than a gateway-side deny/failure.
  cancelledByUser: boolean;
}

// Deep-link param that survives a reload — the order is recoverable by re-fetching
// this id, so a closed tab / refresh never strands the buyer with a VA number they
// can't get back to (dashboard's /payments history is the other durable fallback).
const ORDER_PARAM = 'order';

function readOrderParam(): string | null {
  if (typeof window === 'undefined') return null;
  return new URL(window.location.href).searchParams.get(ORDER_PARAM);
}

function setOrderParam(orderId: string) {
  const url = new URL(window.location.href);
  url.searchParams.set(ORDER_PARAM, orderId);
  window.history.replaceState({}, '', url.toString());
}

function clearOrderParam() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(ORDER_PARAM)) return;
  url.searchParams.delete(ORDER_PARAM);
  window.history.replaceState({}, '', url.toString());
}

function getStatusMeta(status: OrderStatus, t: ReturnType<typeof useTranslations>, cancelledByUser = false) {
  switch (status) {
    case 'PAID':
      return { label: t('orderStatusPaid'), color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', Icon: Check };
    case 'FAILED':
      // A deliberate user cancel is not a malfunction — neutral/muted, not red.
      return cancelledByUser
        ? { label: t('orderStatusCancelled'), color: 'text-slate-600', bg: 'bg-slate-50 border-slate-200', Icon: XCircle }
        : { label: t('orderStatusFailed'), color: 'text-red-700', bg: 'bg-red-50 border-red-200', Icon: AlertCircle };
    case 'EXPIRED':
      return { label: t('orderStatusExpired'), color: 'text-slate-600', bg: 'bg-slate-50 border-slate-200', Icon: Clock };
    case 'REFUNDED':
      return { label: t('orderStatusRefunded'), color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', Icon: AlertCircle };
    default:
      return { label: t('orderStatusPending'), color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', Icon: Clock };
  }
}

// --- CartDrawer ---

export function CartDrawer() {
  const t = useTranslations('cart');
  const { isAuthenticated } = useAuth();
  const { refresh: refreshOwned } = useOwnedItems();
  const {
    items, addItem, removeItem, clearCart, itemCount,
    isDrawerOpen, closeDrawer, openDrawer, notifyCheckoutInitiated,
  } = useCart();

  // Item selection — all selected by default
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [methods, setMethods] = useState<PaymentMethodResponse[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [showMethodPicker, setShowMethodPicker] = useState(false);
  // "Metode lain" toggle for a small order's collapsed BANK_TRANSFER category
  // — see isSmallOrder / SMALL_ORDER_VA_COLLAPSE_THRESHOLD below.
  const [showOtherMethods, setShowOtherMethods] = useState(false);
  const [paying, setPaying] = useState(false);
  // True only for the instant between the pay click and the gateway redirect.
  const [redirectingToGateway, setRedirectingToGateway] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [celebrationItems, setCelebrationItems] = useState<string[] | null>(null);
  const [celebrationTypes, setCelebrationTypes] = useState<PurchasedType[]>([]);
  const [removedCount, setRemovedCount] = useState(0);
  const [priceChangedCount, setPriceChangedCount] = useState(0);
  const [acknowledgedNonRefundable, setAcknowledgedNonRefundable] = useState(false);

  // In-place order tracking — set right after a VA/QRIS/e-wallet order is created (or
  // recovered from the `order` URL param on reload), replaces the cart content/footer
  // with live payment status instead of redirecting to dashboard.aksademia.com.
  const [activeOrder, setActiveOrder] = useState<ActiveOrder | null>(null);

  // Payment disabled state
  const [paymentDisabled, setPaymentDisabled] = useState(false);
  const [paymentDisabledMessage, setPaymentDisabledMessage] = useState<string | null>(null);

  // Discount code state
  const [discountInput, setDiscountInput] = useState('');
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountLabel, setDiscountLabel] = useState<string | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [availableVoucher, setAvailableVoucher] = useState<{ code: string } | null>(null);

  // Live status for the order currently being tracked. No Firebase client SDK exists on
  // this app (session auth is the httpOnly aks_session cookie — see AuthContext), so the
  // SSE endpoint (which requires a Firebase ID token as a query param) isn't reachable
  // from here; useOrderStatusPoll gets the same "flip without a refresh" outcome by
  // polling the already-authenticated GET /payment/orders/:id instead. See its file
  // comment for the full reasoning.
  const liveStatus = useOrderStatusPoll(activeOrder?.id ?? null, activeOrder?.status ?? 'PENDING');

  // Ticks once a second while an order is PENDING — drives the live "Selesaikan dalam"
  // countdown (was frozen between 4s polls before this) and the "Cek Status" button's
  // cooldown display below. Stops (and its interval is torn down) the moment liveStatus
  // leaves PENDING, and on unmount — no timer runs behind a closed drawer or paid order.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (liveStatus !== 'PENDING') return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [liveStatus]);

  // "Cek Status" button — force-checks DOKU for the active order, bypassing the
  // reconciliation sweep's 2-minute-old floor. Server rate-limits to 1 call per 12s per
  // user+order; cooldownEndsAt drives both that limit's cooldown (429, using the
  // server's retryAfterSeconds) and a courtesy client-side 12s cooldown on every click
  // so a second tap doesn't routinely 429 in the first place.
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [checkNotice, setCheckNotice] = useState<string | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const cooldownSecondsLeft = cooldownEndsAt !== null ? Math.max(0, Math.ceil((cooldownEndsAt - now) / 1000)) : 0;

  // A different order (back-to-cart then resume a different pending one, or the
  // "Lanjutkan Pembayaran" 409 flow) must not inherit the previous order's cooldown/
  // notice — the rate limit is scoped per user+order server-side, so a stale client
  // cooldown here would just be wrong, not conservative.
  useEffect(() => {
    setCheckingStatus(false);
    setCheckNotice(null);
    setCheckError(null);
    setCooldownEndsAt(null);
  }, [activeOrder?.id]);

  const handleCheckStatus = useCallback(async () => {
    if (!activeOrder) return;
    const orderId = activeOrder.id;
    setCheckingStatus(true);
    setCheckNotice(null);
    setCheckError(null);

    const DEFAULT_COOLDOWN_SECONDS = 12;
    let cooldownSeconds = DEFAULT_COOLDOWN_SECONDS;

    try {
      const result = await paymentApi.refreshOrderStatus(orderId);
      // Feed the same state useOrderStatusPoll already treats as the source of truth —
      // its `initial` param is activeOrder.status, and it re-syncs liveStatus to match
      // whenever that changes (see its own useEffect). No second status store needed.
      setActiveOrder((prev) => (prev && prev.id === orderId ? {
        ...prev,
        status: result.status as OrderStatus,
        totalAmount: result.totalAmount,
        expiresAt: result.expiresAt,
        instructions: result.paymentInstructions ?? null,
        paymentMethod: result.paymentMethod,
        gateway: result.gateway,
        cancelledByUser: result.cancelledByUser ?? false,
      } : prev));

      if (!result.changed && result.status === 'PENDING') {
        setCheckNotice(t('cekStatusBelumTerbayar'));
      }
    } catch (err) {
      if (err instanceof PaymentApiError && err.status === 429) {
        cooldownSeconds = err.retryAfterSeconds ?? DEFAULT_COOLDOWN_SECONDS;
      } else {
        setCheckError(t('cekStatusGagal'));
      }
    } finally {
      setCheckingStatus(false);
      setCooldownEndsAt(Date.now() + cooldownSeconds * 1000);
    }
  }, [activeOrder, t]);

  // Fetches an order and shows it in-place: PAID goes straight to the celebration
  // overlay, a dead-end PENDING order (hosted-redirect, no native instructions) is
  // dropped silently, everything else renders the status panel. Shared by the reload
  // recovery effect below and the "Lanjutkan Pembayaran" button on a 409 conflict.
  const loadOrderIntoPanel = useCallback(async (orderId: string) => {
    try {
      const order = await paymentApi.getOrder(orderId);
      const titles = order.purchases.map((p) => p.testModule?.title ?? p.course?.title ?? p.bundle?.title ?? p.productTitle ?? '');
      const types = order.purchases.map((p) => purchasedTypeFromProductKind(p.productKind));

      if (order.status === 'PAID') {
        clearOrderParam();
        setCelebrationItems(titles);
        setCelebrationTypes(types);
        refreshOwned();
        openDrawer();
        return;
      }

      const effectiveStatus: OrderStatus = isPaymentClientExpired(order.status, order.expiresAt)
        ? 'EXPIRED'
        : (order.status as OrderStatus);

      if (effectiveStatus === 'PENDING' && !order.paymentInstructions) {
        clearOrderParam();
        return;
      }

      setOrderParam(order.id);
      setActiveOrder({
        id: order.id,
        status: effectiveStatus,
        totalAmount: order.totalAmount,
        expiresAt: order.expiresAt,
        instructions: order.paymentInstructions ?? null,
        paymentMethod: order.paymentMethod,
        purchasedTitles: titles,
        purchasedTypes: types,
        gateway: order.gateway,
        cancelledByUser: order.cancelledByUser ?? false,
      });
      openDrawer();
    } catch {
      // Not found / not ours / network blip — silently fall back to the normal cart
      // rather than dead-ending on an error the buyer can't act on.
      clearOrderParam();
    }
  }, [openDrawer, refreshOwned]);

  // Reload recovery — a closed tab or hard refresh loses all component state, so the
  // only thing that survives is the `order` URL param set right after order creation.
  useEffect(() => {
    const orderId = readOrderParam();
    if (orderId) loadOrderIntoPanel(orderId);
    // Intentionally mount-only: reads the URL directly once, same pattern as
    // CartContext's openCart param handling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The live poll flips PENDING -> PAID without a refresh — hand off to the existing
  // celebration overlay (same flow the free-instant-grant path already uses) and stop
  // tracking this order.
  useEffect(() => {
    if (!activeOrder || liveStatus !== 'PAID') return;
    clearOrderParam();
    setCelebrationItems(activeOrder.purchasedTitles);
    setCelebrationTypes(activeOrder.purchasedTypes);
    refreshOwned();
    setActiveOrder(null);
  }, [liveStatus, activeOrder, refreshOwned]);

  const handleBackToCart = useCallback(() => {
    clearOrderParam();
    setActiveOrder(null);
  }, []);

  // "Batalkan Pesanan" — only rendered when liveStatus === 'PENDING' &&
  // activeOrder.gateway === 'MIDTRANS' (see the button's render condition below and
  // ActiveOrder.gateway's doc comment for why MIDTRANS specifically). A confirm step
  // (showCancelConfirm) gates the actual call — this is destructive and irreversible.
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // A different order must not inherit a previous one's confirm/error state — same
  // reasoning as the "Cek Status" cooldown reset above.
  useEffect(() => {
    setCancelling(false);
    setCancelError(null);
    setShowCancelConfirm(false);
  }, [activeOrder?.id]);

  const handleCancelOrder = useCallback(async () => {
    if (!activeOrder) return;
    const orderId = activeOrder.id;
    setCancelling(true);
    setCancelError(null);

    try {
      const result = await paymentApi.cancelOrder(orderId);
      if (result.cancelled) {
        // Confirmed at the gateway — nothing left to track. Return to the cart so the
        // buyer can start a fresh checkout with a different method.
        clearOrderParam();
        setActiveOrder(null);
        return;
      }
      // The live gateway check found the order already settled (e.g. paid in the
      // instant before this call) and fulfilled it instead of cancelling — reflect
      // the real outcome, same merge shape as handleCheckStatus above. NEVER treat
      // this branch as "cancelled".
      setActiveOrder((prev) => (prev && prev.id === orderId ? {
        ...prev,
        status: result.order.status as OrderStatus,
        totalAmount: result.order.totalAmount,
        expiresAt: result.order.expiresAt,
        instructions: result.order.paymentInstructions ?? null,
        paymentMethod: result.order.paymentMethod,
        gateway: result.order.gateway,
        cancelledByUser: result.order.cancelledByUser ?? false,
      } : prev));
      setShowCancelConfirm(false);
    } catch (err) {
      // Every throw here (CANCEL_FAILED_TRY_AGAIN, CANCEL_UNSUPPORTED,
      // ORDER_NOT_CANCELLABLE, a 429, or a network error) means the order is still
      // PENDING — keep it on screen and offer a retry, never show it as cancelled.
      setCancelError(
        err instanceof PaymentApiError && err.status === 429
          ? t('cancelOrderTerlaluSering')
          : err instanceof PaymentApiError && err.code === 'CANCEL_FAILED_TRY_AGAIN'
            ? t('cancelOrderGagalCoba')
            : t('cancelOrderGagal')
      );
    } finally {
      setCancelling(false);
    }
  }, [activeOrder, t]);

  // Surface an unused referral voucher when the drawer opens. Anonymous visitors
  // get [] from the client, so this quietly no-ops for them and never blocks
  // checkout if the call fails.
  useEffect(() => {
    if (!isDrawerOpen || !isAuthenticated) return;
    referralApi.getMyRewards()
      .then((rewards) => {
        const issued = rewards.find((r) => r.status === 'ISSUED' && r.voucherCode);
        setAvailableVoucher(issued?.voucherCode ? { code: issued.voucherCode } : null);
      })
      .catch(() => {});
  }, [isDrawerOpen, isAuthenticated]);

  // Select all items when drawer opens or items change
  useEffect(() => {
    if (isDrawerOpen) {
      setSelectedIds(new Set(items.map((i) => i.moduleId)));
    }
  }, [isDrawerOpen, items.length]);

  // Derive selected items
  const selectedItems = useMemo(
    () => items.filter((i) => selectedIds.has(i.moduleId)),
    [items, selectedIds]
  );
  const selectedCount = selectedItems.length;
  const hasEventTicket = useMemo(() => selectedItems.some((i) => i.type === 'event'), [selectedItems]);
  // Declared up here (not with the rest of the totals below) so the payment-methods fetch
  // effect can depend on it — it needs the amount before discount is known.
  const subtotalAmount = useMemo(
    () => selectedItems.reduce((sum, i) => sum + i.price, 0),
    [selectedItems]
  );

  const toggleItem = useCallback((moduleId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (selectedCount === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.moduleId)));
    }
  }, [items, selectedCount]);

  // Validate cart items (modules + courses) and detect price changes when drawer opens
  useEffect(() => {
    if (!isDrawerOpen || items.length === 0) return;

    let totalRemoved = 0;
    let totalPriceChanged = 0;

    const moduleItems = items.filter((i) => !i.type || i.type === 'module');
    const courseItems = items.filter((i) => i.type === 'course');
    const bundleItems = items.filter((i) => i.type === 'bundle');

    const validateModules = moduleItems.length > 0
      ? testModuleApi.getPublishedModules().then((published) => {
          const publishedMap = new Map(published.map((m) => [m.id, m]));
          for (const item of moduleItems) {
            const pub = publishedMap.get(item.moduleId);
            if (!pub) {
              removeItem(item.moduleId);
              totalRemoved++;
            } else if (pub.price !== item.price) {
              removeItem(item.moduleId);
              addItem({ ...item, price: pub.price, basePrice: pub.basePrice, promoPrice: pub.promoPrice });
              totalPriceChanged++;
            }
          }
        }).catch(() => {})
      : Promise.resolve();

    const validateCourses = courseItems.length > 0
      ? courseApi.getPublishedCourses().then((published) => {
          const publishedMap = new Map(published.map((c) => [c.id, c]));
          for (const item of courseItems) {
            const pub = publishedMap.get(item.moduleId);
            if (!pub) {
              removeItem(item.moduleId);
              totalRemoved++;
            } else {
              // Unlike TestModule/Bundle (which expose a separate server-computed
              // `price` that's already promo-window gated, and a raw/unconditional
              // `promoPrice`), CoursePublic has no `price`/`effectivePrice` field at
              // all — and course.repository.ts's fetchCoursePricingMap ALREADY nulls
              // out promoPrice outside the promo window before it ever reaches this
              // response (verified in apps/api/src/modules/course/infrastructure/
              // repositories/course.repository.ts). So pub.promoPrice here is not
              // raw — it's the gated value, and falling back to pub.basePrice when
              // it's null is what actually reproduces the effective price. Do NOT
              // "simplify" this to match the module/bundle branches above (there is
              // no pub.price to switch to), and do NOT add client-side
              // promoStartAt/promoEndAt checks — the fields aren't even sent here,
              // because the server already resolved the window.
              const currentPrice = pub.promoPrice ?? pub.basePrice ?? 0;
              if (currentPrice !== item.price) {
                removeItem(item.moduleId);
                addItem({ ...item, price: currentPrice, basePrice: pub.basePrice ?? 0, promoPrice: pub.promoPrice ?? null });
                totalPriceChanged++;
              }
            }
          }
        }).catch(() => {})
      : Promise.resolve();

    const validateBundles = bundleItems.length > 0
      ? bundleApi.getPublishedBundles().then((published) => {
          const publishedMap = new Map(published.map((b) => [b.id, b]));
          for (const item of bundleItems) {
            const pub = publishedMap.get(item.moduleId);
            if (!pub) {
              removeItem(item.moduleId);
              totalRemoved++;
            } else if (pub.price !== item.price) {
              removeItem(item.moduleId);
              addItem({ ...item, price: pub.price, basePrice: pub.basePrice, promoPrice: pub.promoPrice });
              totalPriceChanged++;
            }
          }
        }).catch(() => {})
      : Promise.resolve();

    Promise.all([validateModules, validateCourses, validateBundles]).then(() => {
      setRemovedCount(totalRemoved);
      setPriceChangedCount(totalPriceChanged);
    });
  }, [isDrawerOpen]);

  // Fetch payment methods when drawer opens — but only if the cart actually has something to
  // pay for. Gated on `subtotalAmount` (the selected items' pre-discount total, the best
  // amount known at this point) rather than the cart-wide `totalAmount`, so a method whose
  // minAmount/maxAmount excludes this order never shows up to begin with — the server
  // filters on the same amount (see /payment/methods?amount=). A discount code applied
  // afterwards can still lower the total below a shown method's minAmount; that's still
  // caught at charge time (server re-validates), just not re-filtered out of this list.
  useEffect(() => {
    if (isDrawerOpen && methods.length === 0 && subtotalAmount > 0) {
      setLoadingMethods(true);
      setPaymentDisabled(false);
      setPaymentDisabledMessage(null);
      paymentApi.getPaymentMethods(subtotalAmount)
        .then(setMethods)
        .catch((err: Error) => {
          if (err.message && err.message !== 'Unknown error' && !err.message.startsWith('HTTP ')) {
            setPaymentDisabled(true);
            setPaymentDisabledMessage(err.message);
          }
        })
        .finally(() => setLoadingMethods(false));
    }
  }, [isDrawerOpen, subtotalAmount]);

  // Preselect a method as soon as the list arrives, so the drawer never opens with a
  // disabled "Bayar" button and nothing on screen saying which step is missing.
  //
  // QRIS first: it is the only zero-surcharge channel (Bank Indonesia bars passing its
  // MDR to the buyer), so defaulting there can never silently cost the buyer more than
  // the sticker price. The fallback is any other free method, NOT simply the first in
  // sortOrder — auto-selecting a Rp 4.000 VA the buyer never picked would quietly raise
  // their total. If nothing is free we leave it unselected.
  //
  // Kept byte-identical to the dashboard CartDrawer's copy: these two drawers are the
  // same checkout on two domains and have already drifted once (see the shopeepay
  // callback_url fix), so a change here belongs in both.
  useEffect(() => {
    if (selectedMethod || methods.length === 0) return;
    const free = (m: PaymentMethodResponse) => m.feeFlat === 0 && m.feePercent === 0;
    const preferred = methods.find((m) => m.category === 'QRIS' && free(m)) ?? methods.find(free);
    if (preferred) setSelectedMethod(preferred.code);
  }, [methods, selectedMethod]);

  // Clear discount when selection or cart items change
  useEffect(() => {
    if (discountCode) {
      setDiscountCode(null);
      setDiscountAmount(0);
      setDiscountLabel(null);
    }
  }, [items.length, selectedCount]);

  // Compute remaining totals based on selected items (subtotalAmount is declared earlier —
  // the payment-methods fetch effect above needs it before discount is known).
  const totalAfterDiscount = Math.max(0, subtotalAmount - discountAmount);
  const selectedMethodData = selectedMethod ? methods.find((m) => m.code === selectedMethod) : null;
  // KEEP IN SYNC with calculatePaymentMethodFee in @test-prep/payment-contracts —
  // that's what apps/api's createOrder actually charges (folded into the order
  // total before the gateway invoice is created). This copy is display-only; if
  // it and the backend formula ever disagree, fix THIS one to match that one.
  const fee = selectedMethodData
    ? selectedMethodData.feeFlat + Math.ceil(totalAfterDiscount * selectedMethodData.feePercent / 100)
    : 0;
  const grandTotal = totalAfterDiscount + fee;

  // Group methods by category
  const groupedMethods = useMemo(() => {
    const groups: Record<string, PaymentMethodResponse[]> = {};
    for (const m of methods) {
      if (!groups[m.category]) groups[m.category] = [];
      groups[m.category].push(m);
    }
    return groups;
  }, [methods]);

  const sortedCategories = PAYMENT_CATEGORY_ORDER.filter((c) => groupedMethods[c]);
  const catLabel: Record<string, string> = {
    QRIS: 'QRIS',
    BANK_TRANSFER: 'Transfer Bank',
    E_WALLET: 'E-Wallet',
    RETAIL_OUTLET: 'Gerai Retail',
    CREDIT_CARD: 'Kartu Kredit',
  };

  // Below SMALL_ORDER_VA_COLLAPSE_THRESHOLD a flat VA fee (Rp4,000+) is a much
  // bigger fraction of the total than it is on a large order, so bank-transfer
  // methods start collapsed behind a "Metode lain" toggle and the near-zero-fee
  // QRIS/e-wallet categories are what the buyer sees by default. A method that
  // is already selected (e.g. a returning buyer's preselected VA) is never
  // collapsed — see selectedIsInCollapsedCategory below.
  const isSmallOrder = totalAfterDiscount < SMALL_ORDER_VA_COLLAPSE_THRESHOLD;
  const selectedIsInCollapsedCategory = selectedMethodData?.category === 'BANK_TRANSFER';

  const hasPaidItems = subtotalAmount > 0;
  const needsPaymentMethod = grandTotal > 0;
  const canPay = selectedCount > 0 && !paying
    && (grandTotal === 0 || (!!selectedMethod && !paymentDisabled))
    && (!hasEventTicket || acknowledgedNonRefundable);

  const handleLogin = useCallback(() => {
    const currentPath = window.location.pathname + window.location.search;
    const redirectBack = `${window.location.origin}${currentPath}${currentPath.includes('?') ? '&' : '?'}openCart=true`;
    window.location.href = `${DASHBOARD_URL}/login?redirect=${encodeURIComponent(redirectBack)}`;
  }, []);

  // Accepts an explicit code so the one-click voucher button can validate
  // immediately instead of waiting a render for setDiscountInput to land.
  const handleValidateDiscount = useCallback(async (codeOverride?: string) => {
    const code = (codeOverride ?? discountInput).trim();
    if (!code) return;
    setValidatingDiscount(true);
    setDiscountError(null);

    try {
      const result = await paymentApi.validateDiscountCode({
        code,
        items: selectedItems.map(toOrderItem),
      });

      if (result.valid) {
        setDiscountCode(result.code!);
        setDiscountAmount(result.discountAmount!);
        setDiscountLabel(result.label || `${result.code} (-${result.type === 'PERCENTAGE' ? `${result.value}%` : formatCurrency(result.value!, t('gratis'))})`);
        setDiscountInput('');
      } else {
        const reasons: Record<string, string> = {
          CODE_NOT_FOUND: t('discountReasons.codeNotFound'),
          CODE_EXPIRED: t('discountReasons.codeExpired'),
          CODE_INACTIVE: t('discountReasons.codeInactive'),
          USAGE_LIMIT_REACHED: t('discountReasons.usageLimitReached'),
          ALREADY_USED: t('discountReasons.alreadyUsed'),
          MIN_ORDER_NOT_MET: t('discountReasons.minOrderNotMet'),
          NO_ELIGIBLE_ITEMS: t('discountReasons.noEligibleItems'),
        };
        setDiscountError(reasons[result.reason!] || t('discountReasons.invalid'));
      }
    } catch {
      setDiscountError(t('discountReasons.gagalValidasi'));
    } finally {
      setValidatingDiscount(false);
    }
  }, [discountInput, selectedItems, t]);

  const handleRemoveDiscount = useCallback(() => {
    setDiscountCode(null);
    setDiscountAmount(0);
    setDiscountLabel(null);
    setDiscountInput('');
  }, []);

  // Remove only purchased items from cart, keep the rest
  const removePurchasedItems = useCallback(() => {
    selectedItems.forEach((i) => removeItem(i.moduleId));
  }, [selectedItems, removeItem]);

  const handlePay = useCallback(async () => {
    if (!canPay) return;

    notifyCheckoutInitiated();
    setPaying(true);
    setError(null);
    setNeedsVerification(false);
    setPendingOrderId(null);

    try {
      const idempotencyKey = crypto.randomUUID();

      const orderItems = selectedItems.map(toOrderItem);

      const result = await createOrderRetryingInProgress({
        items: orderItems,
        idempotencyKey,
        paymentMethodCode: selectedMethod || undefined,
        discountCode: discountCode || undefined,
        successRedirectUrl: `${DASHBOARD_URL}/payments?status=success`,
        failureRedirectUrl: `${DASHBOARD_URL}/payments?status=failed`,
      });

      // The order now exists — clear the purchased items out of the cart immediately,
      // regardless of which branch below runs. Waiting for PAID confirmation (DOKU's
      // webhooks can lag minutes) left a paid-for item sitting in the cart, inviting a
      // second order for the same thing. The backend's findBlockingPendingOrder guard
      // (PENDING_ORDER_EXISTS, handled in the catch block below) is the actual
      // double-charge backstop; this is UX cleanliness on top of it — see PR
      // description for the full "remove vs mark pending" reasoning.
      removePurchasedItems();

      if (result.paymentUrl) {
        // Hosted redirect (Snap/Xendit) — an external gateway page, can't be shown
        // in-place. Store purchased item IDs + types so the payments page can show
        // the right CTA.
        sessionStorage.setItem('aksademia_pending_purchase_ids', JSON.stringify(selectedItems.map((i) => i.moduleId)));
        sessionStorage.setItem('aksademia_pending_purchase_types', JSON.stringify(selectedItems.map((i) => i.type ?? 'module')));
        setRedirectingToGateway(true);
        setPaying(false);
        window.location.href = result.paymentUrl;
      } else if (result.paymentInstructions) {
        // Native Core API flow (VA/QRIS/e-wallet) — show instructions + live status
        // right here instead of redirecting to dashboard.aksademia.com.
        setOrderParam(result.orderId);
        setActiveOrder({
          id: result.orderId,
          status: 'PENDING',
          totalAmount: result.totalAmount,
          expiresAt: result.expiresAt,
          instructions: result.paymentInstructions,
          // Server-truth (createOrder now hydrates Order.paymentMethod directly) over the
          // client-selected selectedMethodData — same value in the common case, but
          // authoritative if the server ever resolves a different/default method.
          paymentMethod: result.paymentMethod
            ? { id: result.paymentMethod.id, name: result.paymentMethod.name, iconUrl: result.paymentMethod.iconUrl }
            : selectedMethodData
              ? { id: selectedMethodData.id, name: selectedMethodData.name, code: selectedMethodData.code, iconUrl: selectedMethodData.iconUrl }
              : null,
          purchasedTitles: selectedItems.map((i) => i.title),
          purchasedTypes: selectedItems.map(purchasedTypeFromCartItem),
          gateway: result.gateway ?? null,
          cancelledByUser: false,
        });
        setPaying(false);
      } else {
        // All free — instant grant, show the celebration in place instead of
        // yanking the user off to the dashboard with no feedback.
        refreshOwned();
        setCelebrationTypes(selectedItems.map(purchasedTypeFromCartItem));
        setCelebrationItems(selectedItems.map((i) => i.title));
        setPaying(false);
      }
    } catch (err: unknown) {
      // 409 conflict: one or more selected items are already owned. Show a
      // friendly Indonesian message instead of the raw backend error string,
      // drop the already-owned items from the cart, and refresh owned state
      // so cards/detail pages immediately reflect ownership.
      const ownedIds = err instanceof PaymentApiError
        ? [...(err.ownedModuleIds ?? []), ...(err.ownedCourseIds ?? []), ...(err.ownedBundleIds ?? [])]
        : [];
      if (ownedIds.length > 0) {
        ownedIds.forEach((id) => removeItem(id));
        refreshOwned();
        setError(t('itemSudahDimiliki'));
      } else if (err instanceof PaymentApiError && err.code === 'PENDING_ORDER_EXISTS') {
        // A live PENDING order already covers one of these items. Point at it
        // rather than letting a second parallel order get paid.
        setPendingOrderId(err.pendingOrderId ?? null);
        setError('');
      } else if (err instanceof PaymentApiError && err.code === 'EMAIL_NOT_VERIFIED') {
        // Paid orders require a verified email. Surface a CTA instead of a dead
        // red box — the verify page lives in the dashboard app, so this is a
        // cross-app link rather than a router.push.
        setNeedsVerification(true);
        setError('');
      } else {
        // Backend error strings are English and developer-facing; passing them
        // through put raw text like "Payment gateway error. Please try again."
        // in front of the buyer in an otherwise fully Indonesian checkout.
        // The dashboard drawer already localized these — this keeps the two
        // checkouts saying the same thing for the same failure.
        setError(translatePaymentError(
          err instanceof Error ? { message: err.message, status: (err as PaymentApiError).status } : null,
          t,
        ));
      }
      setPaying(false);
    }
  }, [selectedItems, selectedMethod, selectedMethodData, discountCode, canPay, removePurchasedItems, refreshOwned, removeItem, t, notifyCheckoutInitiated]);

  if (celebrationItems !== null) {
    return (
      <PurchaseCelebration
        items={celebrationItems}
        purchasedTypes={celebrationTypes}
        onClose={() => {
          setCelebrationItems(null);
          setCelebrationTypes([]);
          closeDrawer();
        }}
      />
    );
  }

  if (!isDrawerOpen) return null;

  const statusMeta = activeOrder ? getStatusMeta(liveStatus, t, activeOrder.cancelledByUser) : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-50 transition-opacity"
        onClick={closeDrawer}
      />

      {/* Drawer is deliberately FULL height (h-full, not max-h-full). A short cart
          leaves empty space above the pinned footer — that is normal for a side cart
          (Shopify, Amazon et al all do it) and must NOT be "fixed" by letting the
          panel hug its content: max-h-full makes it float as a card in the corner,
          detached from the viewport edge, which reads as broken. If the emptiness
          ever feels wrong, fill it (continue-shopping link, recommendations) rather
          than shrinking the panel. */}
      <div className="fixed right-0 top-0 h-full w-full sm:max-w-md bg-white z-[60] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-teal-600" />
            <h2 className="text-base font-bold text-slate-900">
              {activeOrder ? t('orderPanelTitle') : t('title')}
              {!activeOrder && itemCount > 0 && (
                <span className="ml-1.5 text-sm font-normal text-slate-400">({itemCount})</span>
              )}
            </h2>
          </div>
          <button
            onClick={closeDrawer}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Stale items warning */}
        {!activeOrder && removedCount > 0 && (
          <div className="flex items-start gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-100 text-xs text-amber-700">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            {t('itemDihapus', { count: removedCount })}
          </div>
        )}

        {/* Price change notice */}
        {!activeOrder && priceChangedCount > 0 && (
          <div className="flex items-start gap-2 px-4 py-2.5 bg-blue-50 border-b border-blue-100 text-xs text-blue-700">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            {t('hargaDiperbarui', { count: priceChangedCount })}
          </div>
        )}

        {/* Content — scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {activeOrder ? (
            <div className="p-4 space-y-4">
              <button
                type="button"
                onClick={handleBackToCart}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {t('orderBackToCart')}
              </button>

              {statusMeta && (
                <div className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 ${statusMeta.bg}`}>
                  <statusMeta.Icon className={`w-4 h-4 flex-shrink-0 ${statusMeta.color}`} />
                  <span className={`text-xs font-semibold ${statusMeta.color}`}>{statusMeta.label}</span>
                </div>
              )}

              {liveStatus === 'PENDING' && activeOrder.expiresAt && (
                <p className="flex items-center gap-1.5 text-xs text-slate-600">
                  <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                  {t('orderCompleteIn')}{' '}
                  <span className="font-semibold text-amber-600 tabular-nums">
                    {formatTimeLeft(activeOrder.expiresAt, t('orderStatusExpired'), now)}
                  </span>
                </p>
              )}

              {liveStatus === 'PENDING' && activeOrder.instructions && (
                <PaymentInstructions instructions={activeOrder.instructions} paymentMethod={activeOrder.paymentMethod} processor={activeOrder.gateway} />
              )}

              {liveStatus === 'PENDING' && (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={handleCheckStatus}
                    disabled={checkingStatus || cooldownSecondsLeft > 0}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-teal-600 text-teal-600 text-xs font-semibold hover:bg-teal-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    {checkingStatus ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        {t('cekStatusMemeriksa')}
                      </>
                    ) : cooldownSecondsLeft > 0 ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5" />
                        {t('cekStatusCooldown', { seconds: cooldownSecondsLeft })}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3.5 h-3.5" />
                        {t('cekStatus')}
                      </>
                    )}
                  </button>

                  {checkNotice && (
                    <p className="text-xs text-slate-500 text-center">{checkNotice}</p>
                  )}
                  {checkError && (
                    <p className="text-xs text-red-600 text-center">{checkError}</p>
                  )}
                </div>
              )}

              {/* Only MIDTRANS implements a safe cancelInvoice today — see
                  ActiveOrder.gateway's doc comment. A null gateway (fresh checkout,
                  not yet hydrated by a getOrder/refresh read) hides the button rather
                  than guess. */}
              {liveStatus === 'PENDING' && activeOrder.gateway === 'MIDTRANS' && (
                <div className="space-y-2">
                  {!showCancelConfirm ? (
                    <button
                      type="button"
                      onClick={() => setShowCancelConfirm(true)}
                      disabled={cancelling}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      {t('cancelOrderButton')}
                    </button>
                  ) : (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2.5">
                      <p className="text-xs text-red-700">{t('cancelOrderConfirm')}</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setShowCancelConfirm(false)}
                          disabled={cancelling}
                          className="flex-1 py-2 rounded-lg border border-slate-300 text-slate-600 text-xs font-semibold hover:bg-slate-100 transition-colors disabled:opacity-50"
                        >
                          {t('cancelOrderBatal')}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelOrder}
                          disabled={cancelling}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          {cancelling && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                          {t('cancelOrderConfirmButton')}
                        </button>
                      </div>
                      {cancelError && (
                        <p className="text-xs text-red-700">{cancelError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {(liveStatus === 'FAILED' || liveStatus === 'EXPIRED' || liveStatus === 'REFUNDED') && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  {liveStatus === 'FAILED'
                    ? (activeOrder?.cancelledByUser ? t('orderCancelledDesc') : t('orderFailedDesc'))
                    : t('orderExpiredDesc')}
                </div>
              )}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 py-10">
              <ShoppingBag className="w-12 h-12 text-slate-200 mb-3" />
              <p className="text-sm font-medium text-slate-500 mb-1">{t('keranjangKosong')}</p>
              <p className="text-xs text-slate-400">{t('tambahkanPaket')}</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Select all toggle */}
              {items.length > 1 && (
                <div className="flex items-center justify-between">
                  <button
                    onClick={toggleAll}
                    className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedCount === items.length ? 'bg-teal-600 border-teal-600' : selectedCount > 0 ? 'bg-teal-100 border-teal-400' : 'border-slate-300'
                    }`}>
                      {selectedCount === items.length && <Check className="w-3 h-3 text-white" />}
                      {selectedCount > 0 && selectedCount < items.length && <div className="w-2 h-0.5 bg-teal-600 rounded" />}
                    </div>
                    {t('pilihSemua')}
                  </button>
                  <span className="text-[10px] text-slate-400">{t('terpilih', { selected: selectedCount, total: items.length })}</span>
                </div>
              )}

              {/* Cart items */}
              <div className="space-y-3">
                {items.map((item) => (
                  <CartItemCard
                    key={item.moduleId}
                    item={item}
                    isSelected={selectedIds.has(item.moduleId)}
                    onToggle={toggleItem}
                    onRemove={removeItem}
                  />
                ))}
              </div>

              {/* Auth prompt for unauthenticated users */}
              {!isAuthenticated && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-700 mb-2">{grandTotal === 0 ? t('masukUntukKlaimBanner') : t('masukUntukBayar')}</p>
                  <button
                    onClick={handleLogin}
                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800 transition-colors"
                  >
                    <LogIn className="w-3.5 h-3.5" />
                    {t('masukDaftar')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — sticky bottom: order status, or payment method + summary + pay button */}
        {(activeOrder || items.length > 0) && (
          <div className="border-t border-slate-200 bg-white">
            {activeOrder ? (
              <div className="p-4 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="font-bold text-slate-900">{t('total')}</span>
                  <span className="font-bold text-slate-900">{formatCurrency(activeOrder.totalAmount, t('gratis'))}</span>
                </div>
                <a
                  href={`${DASHBOARD_URL}/payments/orders/${activeOrder.id}`}
                  className="flex items-center justify-center w-full py-2 text-xs font-semibold text-teal-600 hover:text-teal-700 transition-colors"
                >
                  {t('orderViewHistory')}
                </a>
              </div>
            ) : redirectingToGateway ? (
              // Covers the gap between the pay click and the browser leaving for the
              // gateway. No buttons on purpose — the page is navigating away, so they
              // would be unclickable. The previous block was doubly dead here: nothing
              // ever set the flag that rendered it.
              <div className="p-4 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-900 mb-1">{t('mengalihkan')}</p>
                <p className="text-xs text-slate-500">{t('mengalihkanDesc')}</p>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {/* Discount code input */}
                {isAuthenticated && hasPaidItems && (
                  discountCode ? (
                    <div className="rounded-xl overflow-hidden border border-emerald-200 shadow-sm">
                      <div className="bg-emerald-500 px-3 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Check className="w-3.5 h-3.5 text-white" />
                          <span className="text-xs font-semibold text-white">{t('kodePromoTercantum')}</span>
                        </div>
                        <button
                          onClick={handleRemoveDiscount}
                          className="p-0.5 rounded hover:bg-emerald-600 text-emerald-100 hover:text-white transition-colors"
                          title={t('hapusKode')}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="bg-emerald-50 px-3 py-2.5 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-bold text-emerald-800 truncate">{discountCode}</p>
                          {discountLabel && (
                            <p className="text-[10px] text-emerald-600 truncate mt-0.5">{discountLabel}</p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold text-emerald-700">-{formatCurrency(discountAmount, t('gratis'))}</p>
                          {subtotalAmount > 0 && (
                            <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                              {t('hemat', { pct: Math.round((discountAmount / subtotalAmount) * 100) })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {availableVoucher && (
                        <button
                          onClick={() => { setDiscountInput(availableVoucher.code); handleValidateDiscount(availableVoucher.code); }}
                          className="w-full mb-2 flex items-center justify-between px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs hover:bg-emerald-100 transition-colors cursor-pointer"
                        >
                          <span className="text-emerald-800 font-medium">{t('voucherAvailable', { code: availableVoucher.code })}</span>
                          <span className="text-emerald-700 font-semibold">{t('voucherUse')}</span>
                        </button>
                      )}
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            value={discountInput}
                            onChange={(e) => { setDiscountInput(e.target.value.toUpperCase()); setDiscountError(null); }}
                            onKeyDown={(e) => e.key === 'Enter' && handleValidateDiscount()}
                            placeholder={t('kodePromo')}
                            className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-xs focus:border-teal-500"
                          />
                        </div>
                        <button
                          onClick={() => handleValidateDiscount()}
                          disabled={!discountInput.trim() || validatingDiscount}
                          className="px-3.5 py-2 bg-teal-600 text-white rounded-lg text-xs font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {validatingDiscount ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('cek')}
                        </button>
                      </div>
                      {discountError && (
                        <p className="mt-1.5 text-[10px] text-red-600">{discountError}</p>
                      )}
                    </div>
                  )
                )}

                {/* Payment disabled warning */}
                {paymentDisabled && grandTotal > 0 && (
                  <div className="flex items-start gap-2 p-3 bg-slate-100 rounded-lg text-xs text-slate-600">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-slate-500" />
                    {paymentDisabledMessage || t('pembayaranTidakTersedia')}
                  </div>
                )}

                {/* Payment method picker — collapsed by default, hidden when grandTotal = 0 */}
                {isAuthenticated && needsPaymentMethod && !paymentDisabled && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowMethodPicker(!showMethodPicker)}
                      className="w-full flex items-center justify-between gap-3 px-3.5 py-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors text-left"
                    >
                      {/* Logo + eyebrow label. The collapsed row previously showed a bare
                          "QRIS" behind a generic card glyph, which read as a heading rather
                          than as the method that is about to be charged. Same fixed 48x28
                          logo box as the expanded list, so the picked method looks identical
                          to the row it was picked from. */}
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-7 flex items-center justify-center flex-shrink-0">
                          {selectedMethodData?.iconUrl ? (
                            <Image
                              src={selectedMethodData.iconUrl}
                              alt={selectedMethodData.name}
                              width={48}
                              height={28}
                              unoptimized={!isKnownImageHost(selectedMethodData.iconUrl)}
                              className="w-full h-full object-contain"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <CreditCard className="w-5 h-5 text-slate-300" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            {t('metodePembayaran')}
                          </p>
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {selectedMethodData ? selectedMethodData.name : t('pilihMetodePembayaran')}
                          </p>
                        </div>
                      </div>
                      {/* The collapsed row carries the same fee the expanded list shows, so
                          a preselected method can never hide a surcharge behind an accordion. */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {selectedMethodData && (
                          <span className="text-[11px] text-slate-500 tabular-nums">
                            {formatFeeLabel(selectedMethodData, t('gratis'))}
                          </span>
                        )}
                        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showMethodPicker ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {/* Method list cap sized to fit the realistic method count (2
                        categories, ~6 rows) WITHOUT an inner scrollbar. At max-h-64 a
                        normal 4-method list overflowed and clipped the last category
                        mid-row, producing a second scrollbar inside an already-scrolling
                        drawer while ~250px sat unused above it. Scrolls only for
                        unusually long lists. */}
                    {showMethodPicker && (
                      <div className="mt-1.5 space-y-3 max-h-[60vh] overflow-y-auto pr-0.5">
                        {loadingMethods ? (
                          <div className="space-y-1.5 animate-pulse">
                            {[1, 2, 3].map((i) => (
                              <div key={i} className="rounded-lg border border-slate-200 px-3 py-3 flex items-center gap-2">
                                <div className="w-4 h-4 rounded bg-slate-200" />
                                <div className="h-3 w-24 rounded bg-slate-200" />
                                <div className="ml-auto w-3.5 h-3.5 rounded bg-slate-100" />
                              </div>
                            ))}
                          </div>
                        ) : methods.length > 0 ? sortedCategories.map((category) => {
                          const CategoryIcon = categoryIcons[category] || CreditCard;
                          const catMethods = groupedMethods[category];

                          // Small order + VA category + not already expanded/selected:
                          // render a single "Metode lain" row instead of the full list.
                          const isCollapsedVaCategory =
                            category === 'BANK_TRANSFER' && isSmallOrder
                            && !showOtherMethods && !selectedIsInCollapsedCategory;

                          if (isCollapsedVaCategory) {
                            return (
                              <button
                                key={category}
                                type="button"
                                onClick={() => setShowOtherMethods(true)}
                                aria-expanded={showOtherMethods}
                                aria-controls="payment-method-bank-transfer-list"
                                className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-left bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                              >
                                <span className="flex items-center gap-1.5 text-xs text-slate-500">
                                  <CategoryIcon className="w-3.5 h-3.5 text-slate-400" />
                                  {t('metodeLain')}
                                </span>
                                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                              </button>
                            );
                          }

                          // Flattened: a static section label, not a second collapsible
                          // level — the old category-accordion-inside-picker-accordion
                          // meant two taps to reach any of a category's three methods.
                          return (
                            <div key={category}>
                              <div className="flex items-center gap-1.5 px-1 mb-1.5">
                                <CategoryIcon className="w-3.5 h-3.5 text-slate-400" />
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                  {catLabel[category] ?? category}
                                </span>
                              </div>
                              <div
                                id={category === 'BANK_TRANSFER' ? 'payment-method-bank-transfer-list' : undefined}
                                className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden"
                              >
                                {catMethods.map((method) => {
                                  const isSelected = selectedMethod === method.code;
                                  return (
                                    <button
                                      key={method.code}
                                      type="button"
                                      onClick={() => {
                                        setSelectedMethod(method.code);
                                        setShowMethodPicker(false);
                                      }}
                                      className={`w-full flex items-center justify-between gap-3 px-3.5 py-3 text-left transition-colors ${
                                        isSelected ? 'bg-teal-50' : 'hover:bg-slate-50'
                                      }`}
                                    >
                                      <div className="flex items-center gap-3 min-w-0">
                                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                          isSelected ? 'border-teal-600' : 'border-slate-300'
                                        }`}>
                                          {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-teal-600" />}
                                        </div>
                                        {/* Logo slot: a FIXED 48x28 box, always reserved.
                                            Two things depend on this being fixed in BOTH dimensions:
                                            1. Alignment — a method with no iconUrl (e.g. "Transfer Bank
                                               Lain") would otherwise skip straight to its label and sit
                                               at a different left offset from methods that have a logo.
                                            2. Symmetry — bank marks have wildly different aspect ratios
                                               (the `mandiri` wordmark is very wide, `bca` nearly square).
                                               Locking only the height and letting width float made each
                                               logo occupy a different footprint, so the column read as
                                               ragged. object-contain inside a fixed box letterboxes each
                                               mark into identical space without distorting it. */}
                                        <div className="w-12 h-7 flex items-center justify-center flex-shrink-0">
                                          {method.iconUrl ? (
                                            <Image
                                              src={method.iconUrl}
                                              alt={method.name}
                                              width={48}
                                              height={28}
                                              unoptimized={!isKnownImageHost(method.iconUrl)}
                                              className="w-full h-full object-contain"
                                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            />
                                          ) : (
                                            <CategoryIcon className="w-5 h-5 text-slate-300" />
                                          )}
                                        </div>
                                        <span className="text-xs text-slate-800 truncate">{method.name}</span>
                                      </div>
                                      <span className="text-[11px] text-slate-500 flex-shrink-0 tabular-nums">
                                        {formatFeeLabel(method, t('gratis'))}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }) : (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                            {t('metodeBelumTersedia')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Summary */}
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('subtotal', { count: selectedCount })}</span>
                    <span className="text-slate-900 font-medium">{formatCurrency(subtotalAmount, t('gratis'))}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-1.5">
                        <span className="text-emerald-600 font-medium">{t('diskon')}</span>
                        <span className="text-[10px] font-mono font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                          {discountCode}
                        </span>
                      </div>
                      <span className="text-emerald-600 font-semibold">-{formatCurrency(discountAmount, t('gratis'))}</span>
                    </div>
                  )}
                  {selectedMethodData && fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('biaya', { name: selectedMethodData.name })}</span>
                      <span className="text-slate-900 font-medium">{formatCurrency(fee, t('gratis'))}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1.5 border-t border-slate-100">
                    <span className="font-bold text-slate-900">{t('total')}</span>
                    <span className="font-bold text-slate-900">{formatCurrency(grandTotal, t('gratis'))}</span>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 p-2 bg-red-50 rounded-lg text-xs text-red-700">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}

                {/* An unfinished payment already covers one of these items. */}
                {pendingOrderId !== null && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                      <p className="text-xs leading-relaxed text-amber-800">
                        {t('pendingOrderExists')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => pendingOrderId && loadOrderIntoPanel(pendingOrderId)}
                      className="mt-2 inline-flex w-full items-center justify-center rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
                    >
                      {t('pendingOrderCta')}
                    </button>
                  </div>
                )}

                {/* Unverified email blocks paid orders — give them the way out
                    rather than a red box they can't act on. */}
                {needsVerification && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                      <p className="text-xs leading-relaxed text-amber-800">
                        {t('verifyEmailRequired')}
                      </p>
                    </div>
                    <a
                      href={`${DASHBOARD_URL}/verify-email?reason=checkout`}
                      className="mt-2 inline-flex w-full items-center justify-center rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
                    >
                      {t('verifyEmailCta')}
                    </a>
                  </div>
                )}

                {/* Non-refundable acknowledgment — gates the pay button while an
                    event ticket is in the cart. The ticked box is the chargeback
                    evidence, so its state feeds canPay directly (see above). */}
                {isAuthenticated && hasEventTicket && (
                  <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acknowledgedNonRefundable}
                      onChange={(e) => setAcknowledgedNonRefundable(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-xs text-amber-800">{t('ackNonRefundable')}</span>
                  </label>
                )}

                {/* Pay / Login button */}
                {isAuthenticated ? (
                  paymentDisabled && grandTotal > 0 ? (
                    <button
                      disabled
                      className="w-full py-3 rounded-xl font-bold text-sm bg-slate-300 text-slate-500 cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {t('pembayaranTidakTersediaBtn')}
                    </button>
                  ) : (
                    <button
                      onClick={handlePay}
                      disabled={!canPay}
                      className="w-full py-3 rounded-xl font-bold text-sm bg-orange-500 text-white hover:bg-orange-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {paying ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t('memproses')}
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          {grandTotal === 0 ? t('dapatkanGratis') : t('bayar', { amount: formatCurrency(grandTotal, t('gratis')) })}
                        </>
                      )}
                    </button>
                  )
                ) : (
                  <button
                    onClick={handleLogin}
                    className="w-full py-3 rounded-xl font-bold text-sm bg-teal-600 text-white hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <LogIn className="w-4 h-4" />
                    {grandTotal === 0 ? t('masukUntukKlaim') : t('masukUntukMembeli')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// --- CartItemCard ---

function CartItemCard({ item, isSelected, onToggle, onRemove }: {
  item: CartItem;
  isSelected: boolean;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const t = useTranslations('cart');
  // item.price is the already-computed effective price (server-side promo-window
  // gated, same semantics as TestModulePublic.price) — item.promoPrice is RAW and
  // unconditional for module/bundle/event items (not gated on promoStartAt/
  // promoEndAt). hasPromo must compare against item.price, and the badge must
  // render item.price, not promoPrice directly: outside the promo window the
  // server collapses price back down to basePrice, so `price < basePrice` is the
  // only reliable "is discounted" signal, and item.price is the only number that
  // matches what checkout actually charges. Mirrors dashboard's CartItemCard.
  const hasPromo = item.promoPrice !== null && item.basePrice > 0 && item.price < item.basePrice;
  const discount = hasPromo ? Math.round(((item.basePrice - item.price) / item.basePrice) * 100) : 0;
  const isModule = !item.type || item.type === 'module';
  const isEvent = item.type === 'event';
  const isBundle = item.type === 'bundle';

  return (
    <div className={`flex gap-3 rounded-xl border p-3 transition-colors ${isSelected ? 'bg-white border-slate-200 hover:border-slate-300' : 'bg-slate-50 border-dashed border-slate-200 opacity-60'}`}>
      {/* Checkbox */}
      <button
        onClick={() => onToggle(item.moduleId)}
        className="mt-1 flex-shrink-0"
      >
        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
          isSelected ? 'bg-teal-600 border-teal-600' : 'border-slate-300 hover:border-slate-400'
        }`}>
          {isSelected && <Check className="w-3 h-3 text-white" />}
        </div>
      </button>

      {/* Thumbnail */}
      <div className="relative w-14 h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
        {item.coverImageUrl ? (
          <Image
            src={item.coverImageUrl}
            alt={item.title}
            fill
            sizes="56px"
            className="object-cover"
            // A failed/malformed source used to leave whatever the source partially
            // rendered (e.g. a fragment of a wide banner graphic cropped into this
            // square slot) — hide it and fall back to the branded placeholder instead.
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <CoverPlaceholder label="" compact />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {/* One neutral treatment for all item types — the label text (Ujian,
                Belajar, ...) already carries the distinction. Four arbitrary hues
                (teal/blue/indigo/purple) here were off-brand noise; this app is
                teal + orange only (see DESIGN_GUIDELINES.md). */}
            <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full mb-1 bg-slate-100 text-slate-600">
              {isEvent ? t('liveEvent') : isModule ? t('paketUjian') : isBundle ? t('paketLengkap') : t('paketBelajar')}
            </span>
            <p className="text-sm font-semibold text-slate-900 leading-tight line-clamp-2">{item.title}</p>
          </div>
          <button
            onClick={() => onRemove(item.moduleId)}
            className="p-1 rounded-md hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          {item.isFree ? (
            <span className="text-xs font-bold text-emerald-600">{t('gratis')}</span>
          ) : hasPromo ? (
            <>
              <span className="text-sm font-bold text-slate-900">{formatCurrency(item.price, t('gratis'))}</span>
              <span className="text-[10px] text-slate-400 line-through">{formatCurrency(item.basePrice, t('gratis'))}</span>
              <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">-{discount}%</span>
            </>
          ) : (
            <span className="text-sm font-bold text-slate-900">{formatCurrency(item.price, t('gratis'))}</span>
          )}
        </div>
      </div>
    </div>
  );
}
